import { Helmet } from 'react-helmet';
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './SignageCalendar.css';
import facilities from '../../config/facilities.json';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase/config';

import { ReactComponent as Leaves1 } from '../../assets/leaves1.svg';
import { ReactComponent as Leaves2 } from '../../assets/leaves2.svg';
import burger from '../../assets/burger.png';
import pizza from '../../assets/pizza.png';
import spaghetti from '../../assets/spaghetti.png';
import { getUTCDate, getNewYorkTimeZoneDate } from '../../utils/dayjsUs';
import dayjs from 'dayjs';
import Slideshow from '../../Components/Slideshow/Slideshow';
import { getImageURL } from '../../firebase/functions';

function SignageCalendar() {
  /** URL params
   * facility: facility id (admirals-pointe) REQUIRED
   * interval: slideshow swap interval in seconds OPTIONAL (90)
   * only: display only specific slide (meals, activities, memoryCare) OPTIONAL
   */

  const [searchParams] = useSearchParams();
  const intervalParam = searchParams.get('interval');
  const interval =
    Number(intervalParam) > 0 ? Number(intervalParam) * 1000 : 90000;

  const facilityId = searchParams.get('facility');
  const onlySlide = searchParams.get('only');

  const [facility, setFacility] = useState({});

  const slides = ['meals', 'activities', 'memoryCare'].filter((x) =>
    onlySlide ? x === onlySlide : true,
  );
  const [currentSlide, setCurrentSlide] = useState(slides[0]);
  const [today, setToday] = useState(getNewYorkTimeZoneDate().startOf('day'));
  const [currentTime, setCurrentTime] = useState(
    getNewYorkTimeZoneDate().format('h:mm'),
  );
  const [currentTimeAMPM, setCurrentTimeAMPM] = useState(
    getNewYorkTimeZoneDate().format('A'),
  );
  const [nowNY, setNowNY] = useState(getNewYorkTimeZoneDate());

  const [facilityData, setFacilityData] = useState({});

  const [calendarImages, setCalendarImages] = useState([]);

  useEffect(() => {
    const found = facilities.find((x) => x.id === facilityId);
    if (!found) {
      window.alert('Invalid facility!');
      return;
    }
    setFacility(found);
  }, [facilityId]);

  // Switch slides in intervals
  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentSlide((prevSlide) => {
        const currentIndex = slides.indexOf(prevSlide);
        const nextIndex = (currentIndex + 1) % slides.length;
        return slides[nextIndex];
      });
    }, interval);

    return () => clearInterval(timerId);
  }, [interval, slides]);

  useEffect(() => {
    let currentDate = getNewYorkTimeZoneDate().format('YYYY-MM-DD');
    const interval = setInterval(
      () => {
        const newDate = getNewYorkTimeZoneDate().format('YYYY-MM-DD');
        if (newDate !== currentDate) {
          window.location.reload(); // Refresh page at midnight
        }
        currentDate = newDate; // Update current date
        setToday(getNewYorkTimeZoneDate());
      },
      60 * 60 * 1000, // 1 hour
    );

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const ny = getNewYorkTimeZoneDate();
      setNowNY(ny);
      setCurrentTime(ny.format('h:mm'));
      setCurrentTimeAMPM(ny.format('A'));
    }, 1000); // update every second

    // cleanup function to clear the interval when the component unmounts
    return () => clearInterval(interval);
  }, []);

  const [activities, setActivitites] = useState([]);
  const [meals, setMeals] = useState([]);
  const [corporateMealsToday, setCorporateMealsToday] = useState(null);
  const [memoryCareActivities, setMemoryCareActivities] = useState([]);
  const [alternates, setAlternates] = useState([]);

  const weekDays = useMemo(() => {
    let startOfWeek = today.startOf('day');
    return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
  }, [today]);

  const isEventOnNyDay = (seconds, nyDay) => {
    const eventDayStr = getUTCDate(dayjs.unix(seconds)).format('YYYY-MM-DD');
    const nyDayStr = nyDay.format('YYYY-MM-DD');
    return eventDayStr === nyDayStr;
  };

  const fetchMealsData = () => {
    const startNyStr = today.startOf('day').format('YYYY-MM-DD');
    const nextNyStr = today.add(1, 'day').startOf('day').format('YYYY-MM-DD');
    const startUtc = dayjs.utc(`${startNyStr} 00:00`).toDate();
    const nextStartUtc = dayjs.utc(`${nextNyStr} 00:00`).toDate();
    const mealsConditions = [
      where('facilityId', '==', facilityId),
      where('type', '==', 'meals'),
      where('date', '>=', startUtc),
      where('date', '<', nextStartUtc),
      orderBy('date', 'asc'),
    ];

    const qm = query(collection(db, 'calendar'), ...mealsConditions);
    const unsubscribeMeals = onSnapshot(
      qm,
      (querySnapshot) => {
        const fetched = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        try {
          fetched.forEach((m) => {
            const d = m?.date?.toDate ? m.date.toDate() : m.date;
          });
        } catch {
          /* no-op: logging is best-effort */
        }
        setMeals(fetched);
      },
      (error) => {
        console.error('[SignageMeals] onSnapshot error', error);
      },
    );
    // Also subscribe to today's corporate meals doc for this facility
    const nyDateKey = today.startOf('day').format('YYYY-MM-DD');
    const corpDocRef = doc(
      db,
      'facilityCorporateMeals',
      facilityId,
      'days',
      nyDateKey,
    );
    const unsubscribeCorporate = onSnapshot(
      corpDocRef,
      (snap) => {
        setCorporateMealsToday(snap.exists() ? snap.data() : null);
      },
      (error) => {
        console.error('[SignageMeals] corporate doc error', error);
      },
    );

    return () => {
      unsubscribeMeals();
      unsubscribeCorporate();
    };
  };
  const fetchActivitiesData = () => {
    const startNyStr = today.startOf('day').format('YYYY-MM-DD');
    const endNyStr = today.add(7, 'days').startOf('day').format('YYYY-MM-DD');
    const startUtc = dayjs.utc(`${startNyStr} 00:00`).toDate();
    const endUtc = dayjs.utc(`${endNyStr} 00:00`).toDate();
    const activitiesConditions = [
      where('facilityId', '==', facilityId),
      where('type', '==', 'activities'),
      where('date', '>=', startUtc),
      where('date', '<=', endUtc),
      orderBy('date', 'asc'),
      orderBy('timeFrom', 'asc'),
    ];

    const qa = query(collection(db, 'calendar'), ...activitiesConditions);
    const unsubscribeActivities = onSnapshot(qa, (querySnapshot) => {
      const activities = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setActivitites(activities);
    });

    return unsubscribeActivities;
  };
  const fetchAlternatesData = () => {
    const monthNyStr = today.startOf('month').format('YYYY-MM-DD');
    const monthUtcMidnight = dayjs.utc(`${monthNyStr} 00:00`).toDate();
    const altConditions = [
      where('facilityId', '==', facilityId),
      where('type', '==', 'meals'),
      where('date', '==', monthUtcMidnight),
      where('mealType', '==', 'alternate'),
    ];

    const qalt = query(collection(db, 'calendar'), ...altConditions);
    const unsubscribeAlternates = onSnapshot(qalt, (querySnapshot) => {
      const alternates = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setAlternates(alternates?.[0] ?? null);
    });

    return unsubscribeAlternates;
  };
  const fetchMemoryCareData = () => {
    const startNyStr = today.startOf('day').format('YYYY-MM-DD');
    const endNyStr = today.add(7, 'days').startOf('day').format('YYYY-MM-DD');
    const startUtc = dayjs.utc(`${startNyStr} 00:00`).toDate();
    const endUtc = dayjs.utc(`${endNyStr} 00:00`).toDate();
    const memoryCareConditions = [
      where('facilityId', '==', facilityId),
      where('type', '==', 'memoryCareActivities'),
      where('date', '>=', startUtc),
      where('date', '<=', endUtc),
      orderBy('date', 'asc'),
      orderBy('timeFrom', 'asc'),
    ];

    const qmc = query(collection(db, 'calendar'), ...memoryCareConditions);
    const unsubscribeMemoryCare = onSnapshot(qmc, (querySnapshot) => {
      const memoryCareActivities = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setMemoryCareActivities(memoryCareActivities);
    });

    return unsubscribeMemoryCare;
  };

  const getCalendarImages = () => {
    // First query: fetch images with the specified facilityId, ordered by uploaded date
    const facilityIdQuery = query(
      collection(db, 'images'),
      where('facility', '==', facilityId),
      orderBy('uploaded', 'desc'),
    );
    // Use onSnapshot to get real-time updates from both queries
    const unsubscribeFacility = onSnapshot(
      facilityIdQuery,
      (facilitySnapshot) => {
        // Filter documents that do not have the 'month' field
        const facilityImages = facilitySnapshot.docs
          .filter(
            (doc) => !Object.prototype.hasOwnProperty.call(doc.data(), 'month'),
          )
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        const selectedCalendarImages = facilityImages.filter(
          (image) => !image.type && image.selected,
        );
        setCalendarImages(selectedCalendarImages);
      },
    );

    // Return a cleanup function to unsubscribe from both onSnapshot listeners
    return () => {
      unsubscribeFacility();
    };
  };
  const getFacilityData = () => {
    const facilityQuery = query(
      collection(db, 'facilities'),
      where('facilityId', '==', facilityId),
      where('month', '==', today.format('MMMM')),
    );
    const unsubscribeFacility = onSnapshot(
      facilityQuery,
      async (querySnapshot) => {
        const facilities = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        setFacilityData({
          ...facilities?.[0],
        });
      },
    );

    return () => {
      unsubscribeFacility();
    };
  };

  useEffect(() => {
    if (!facilityId) {
      return;
    }

    // Set up subscriptions
    const unsubscribeImages = getCalendarImages();
    const unsubscribeFacility = getFacilityData();
    const unsubscribeMeals = fetchMealsData();
    const unsubscribeActivities = fetchActivitiesData();
    const unsubscribeAlternates = fetchAlternatesData();
    const unsubscribeMemoryCare = fetchMemoryCareData();

    // Clean up subscriptions on unmount
    return () => {
      unsubscribeImages();
      unsubscribeFacility();
      unsubscribeMeals();
      unsubscribeActivities();
      unsubscribeAlternates();
      unsubscribeMemoryCare();
    };
  }, [facilityId, today]);

  return (
    <div
      style={{
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        '--facilityDarkColor': facility.darkColor,
        '--facilityLightColor': facility.lightColor,
        '--facilitytextColor': facility.textColor,
      }}
      className="calendarSlideshow"
    >
      {currentSlide !== 'meals' && (
        <div
          className="calendarSlideshowOverlay"
          style={{
            backgroundImage: `url(${facilityData?.calendarTemplateImage})`,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      )}
      <Helmet>
        <style>{`
        body {
          scrollbar-width: none; /* For Firefox */
          -ms-overflow-style: none;  /* For Internet Explorer and Edge */
        }
        ::-webkit-scrollbar {
          display: none; /* For Chrome, Safari and Opera */
        }
      `}</style>
      </Helmet>
      {!facilityData?.calendarTemplateImage && (
        <>
          <Leaves1
            className={`slideshowSVG ${currentSlide === 'activities' ? 'topleftSVG' : 'toprightSVG'}`}
          />
          <Leaves2
            className={`slideshowSVG ${currentSlide === 'activities' ? 'bottomrightSVG' : 'bottomleftSVG'}`}
          />
        </>
      )}
      {currentSlide === 'activities' && (
        <Box
          className="slideshowWrapper"
          style={{
            padding: facilityData?.calendarTemplateImage
              ? '15vh 9vw'
              : '3vh 2vw',
          }}
        >
          <div className="calendarSlideshowActivities">
            <div className="calendarSlideshowImageTitle">
              {calendarImages.filter((img) => img.selected).length > 0 && (
                <div className="slideshowFrame">
                  <div
                    className="slideshowFrameOverlay"
                    style={{
                      backgroundImage: `url(${facilityData?.calendarFrameImage})`,
                    }}
                  />
                  <div className="slideshowImagesContainer">
                    <Slideshow
                      images={calendarImages}
                      facilityId={facilityId}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="calendarSlideshowTodayEvent">
              <div className="calendarSlideshowTodayTitle">
                <h1 className="titleToday">Today’s</h1>
                <h1 className="titleType">Activities</h1>

                <div className="slideshowDateContainer">
                  <div className="slideshowClock">
                    <span className="time">{currentTime}</span>
                    <span className="ampm">{currentTimeAMPM}</span>
                  </div>
                  <div className="slideshowDate">
                    <p>
                      {nowNY.format('dddd')}, {nowNY.format('MMMM Do')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="calendarSlideshowTodayEventDetails">
                {activities
                  .filter((activity) =>
                    isEventOnNyDay(activity.date.seconds, today),
                  )
                  .map((activity, index) => (
                    <div key={index}>
                      {getUTCDate(dayjs.unix(activity.timeFrom.seconds)).format(
                        'hh:mm',
                      )}{' '}
                      {activity.name}
                    </div>
                  ))}
              </div>
            </div>
            {weekDays
              .filter((day) => !day.isSame(today, 'day'))
              .map((day, index) => (
                <div key={index} className="calendarSlideshowDayActivity">
                  <h3>
                    {day.isSame(today.add(1, 'day'), 'day')
                      ? 'Tomorrow'
                      : day.format('dddd')}
                  </h3>
                  <div className="calendarSlideshowDayActivities">
                    {activities
                      .filter((activity) =>
                        isEventOnNyDay(activity.date.seconds, day),
                      )
                      .map((activity, index) => (
                        <div key={index}>
                          {getUTCDate(
                            dayjs.unix(activity.timeFrom.seconds),
                          ).format('hh:mm')}{' '}
                          {activity.name}
                        </div>
                      ))}
                  </div>
                  <div className="calendarSlideshowDayDate">
                    <p>{day.format('MMMM Do')}</p>
                  </div>
                </div>
              ))}
          </div>
        </Box>
      )}

      {currentSlide === 'meals' && (
        <Box className="mealsContainer">
          <div className="mealsTitleImagesSection">
            <div className="mealsTitleImagesSectionTitle">
              <h1 className="todays">Today’s</h1>
              <h1>MENU</h1>
            </div>

            <div className="mealsImagesContainer">
              <div className="mealsTitleImage">
                <img src={burger} alt="Burger" />
              </div>
              <div className="mealsTitleImage">
                <img src={spaghetti} alt="Spaghetti" />
              </div>
              <div className="mealsTitleImage">
                <img src={pizza} alt="Pizza" />
              </div>
            </div>
          </div>
          <div className="calendarSlideshowMeals">
            <div className="calendarSlideshowBreakfast">
              <h1 className="calendarSlideshowMealTitle">Breakfast</h1>
              <div className="calendarSlideshowMealsContainer">
                {/* Corporate items first (visible + local), then local calendar text */}
                {(() => {
                  const corp = corporateMealsToday?.breakfast;
                  const visible = (corp?.corporateItems || []).filter(
                    (item) => !(corp?.hiddenItems || []).includes(item),
                  );
                  const locals = corp?.localItems || [];
                  return [...visible, ...locals].map((item, idx) => (
                    <p key={`c-b-${idx}`}>{item}</p>
                  ));
                })()}
                {meals
                  .filter((m) => m.mealType === 'breakfast')
                  .flatMap((meal) => meal.name.split('\n'))
                  .map((meal, index) => (
                    <p key={`l-b-${index}`}>{meal}</p>
                  ))}
              </div>
            </div>
            <div className="calendarSlideshowLunch">
              <h1 className="calendarSlideshowMealTitle">Lunch</h1>
              <div className="calendarSlideshowMealsContainer">
                {(() => {
                  const corp = corporateMealsToday?.lunch;
                  const visible = (corp?.corporateItems || []).filter(
                    (item) => !(corp?.hiddenItems || []).includes(item),
                  );
                  const locals = corp?.localItems || [];
                  return [...visible, ...locals].map((item, idx) => (
                    <p key={`c-l-${idx}`}>{item}</p>
                  ));
                })()}
                {meals
                  .filter((m) => m.mealType === 'lunch')
                  .flatMap((meal) => meal.name.split('\n'))
                  .map((meal, index) => (
                    <p key={`l-l-${index}`}>{meal}</p>
                  ))}
              </div>
            </div>
            <div className="calendarSlideshowDinner">
              <h1 className="calendarSlideshowMealTitle">Dinner</h1>
              <div className="calendarSlideshowMealsContainer">
                {(() => {
                  const corp = corporateMealsToday?.dinner;
                  const visible = (corp?.corporateItems || []).filter(
                    (item) => !(corp?.hiddenItems || []).includes(item),
                  );
                  const locals = corp?.localItems || [];
                  return [...visible, ...locals].map((item, idx) => (
                    <p key={`c-d-${idx}`}>{item}</p>
                  ));
                })()}
                {meals
                  .filter((m) => m.mealType === 'dinner')
                  .flatMap((meal) => meal.name.split('\n'))
                  .map((meal, index) => (
                    <p key={`l-d-${index}`}>{meal}</p>
                  ))}
              </div>
            </div>
            <div className="calendarSlideshowAlternates">
              <h1 className="calendarSlideshowMealTitle">Alternates</h1>
              <div className="calendarSlideshowMealsContainer">
                {alternates?.name &&
                  alternates.name
                    .split('\n')
                    .map((alt, index) => <p key={index}>{alt}</p>)}
              </div>
            </div>
          </div>
        </Box>
      )}

      {currentSlide === 'memoryCare' && (
        <Box
          className="slideshowWrapper"
          style={{
            padding: facilityData?.calendarTemplateImage
              ? '15vh 9vw'
              : '3vh 2vw',
          }}
        >
          <div className="calendarSlideshowActivities">
            <div className="calendarSlideshowImageTitle">
              {calendarImages.filter((img) => img.selected).length > 0 && (
                <div className="slideshowFrame">
                  <div
                    className="slideshowFrameOverlay"
                    style={{
                      backgroundImage: `url(${facilityData?.calendarFrameImage})`,
                    }}
                  />
                  <div className="slideshowImagesContainer">
                    <Slideshow
                      images={calendarImages}
                      facilityId={facilityId}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="calendarSlideshowTodayEvent">
              <div className="calendarSlideshowTodayTitle">
                <h1 className="titleToday">Today’s</h1>
                <h1 className="titleType">Activities</h1>

                <div className="slideshowDateContainer">
                  <div className="slideshowClock">
                    <span className="time">{currentTime}</span>
                    <span className="ampm">{currentTimeAMPM}</span>
                  </div>
                  <div className="slideshowDate">
                    <p>
                      {nowNY.format('dddd')}, {nowNY.format('MMMM Do')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="calendarSlideshowTodayEventDetails">
                {memoryCareActivities
                  .filter((memoryCareActivity) =>
                    isEventOnNyDay(memoryCareActivity.date.seconds, today),
                  )
                  .map((memoryCareActivity, index) => (
                    <div key={index}>
                      {getUTCDate(
                        dayjs.unix(memoryCareActivity.timeFrom.seconds),
                      ).format('hh:mm')}{' '}
                      {memoryCareActivity.name}
                    </div>
                  ))}
              </div>
            </div>
            {weekDays
              .filter((day) => !day.isSame(today, 'day'))
              .map((day, index) => (
                <div key={index} className="calendarSlideshowDayActivity">
                  <h3>
                    {day.isSame(today.add(1, 'day'), 'day')
                      ? 'Tomorrow'
                      : day.format('dddd')}
                  </h3>
                  <div className="calendarSlideshowDayActivities">
                    {memoryCareActivities
                      .filter((memoryCareActivity) =>
                        isEventOnNyDay(memoryCareActivity.date.seconds, day),
                      )
                      .map((memoryCareActivity, index) => (
                        <div key={index}>
                          {getUTCDate(
                            dayjs.unix(memoryCareActivity.timeFrom.seconds),
                          ).format('hh:mm')}{' '}
                          {memoryCareActivity.name}
                        </div>
                      ))}
                  </div>
                  <div className="calendarSlideshowDayDate">
                    <p>{day.format('MMMM Do')}</p>
                  </div>
                </div>
              ))}
          </div>
        </Box>
      )}
    </div>
  );
}

SignageCalendar.propTypes = {};

export default SignageCalendar;
